// src/app/modules/user/modules/accounts/pages/DoctorDetailsPage.tsx
import React, {useState, useEffect, useCallback} from 'react'
import {Navigate, Route, Routes, Outlet, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../../_metronic/layout/core'
import {ProfileDetailOverview} from '../../components/profile-detail/ProfileDetailOverview'
import DoctorForm from '../../components/forms/DoctorForm'
import {toast} from 'react-toastify'
import axios from 'axios'
import {ProfileDetailHeader} from '../../components/profile-detail/ProfileDetailHeader'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import DoctorProfileDevicesPage from './DoctorProfileDevicesPage'
import AllPatientsListingProfilePage from './AllPatientsListingProfilePage'
import DoctorPaymentHistory from './DoctorPaymentHistory'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import RoutesRoleGuard from '../../components/permissions/RoutesPermissionGuard'
import {FaDollarSign, FaMobileAlt, FaUser} from 'react-icons/fa'
import {FiUsers} from 'react-icons/fi'
import Loader from '../../components/Loader'

interface LinkItem {
  title: string
  path: string
  label: string
  allowedRoles: number[]
  icon?: React.ReactNode
}

const DoctorDetailsPage: React.FC = () => {
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const {doctorId} = useParams<{doctorId: string}>()
  const [userData, setUserData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  
  const fetchDetails = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(API_ENDPOINTS.GET_SPECIFIC_USER(doctorId))
      setUserData(res.data.data?.user)
    } catch (err) {
      notifyError('Failed to load doctor details.')
    } finally {
      setLoading(false)
    }
  }, [doctorId])

  useEffect(() => {
    fetchDetails()
  }, [doctorId])

  const accountBreadCrumbs: PageLink[] = [
    {
      title: 'Doctor Details',
      path: `doctor-details/${doctorId}`,
      isSeparator: false,
      isActive: false,
    },
    {title: '', path: '', isSeparator: true, isActive: false},
  ]

  const links: LinkItem[] = [
    {
      title: 'Patients',
      path: `/doctor-details/${doctorId}/patients`,
      label: 'patients',
      allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
      icon: <FiUsers />,
    },
    {
      title: 'Devices',
      path: `/doctor-details/${doctorId}/devices`,
      label: 'devices',
      allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
      icon: <FaMobileAlt />,
    },
    {
      title: 'Payment History',
      path: `/doctor-details/${doctorId}/doctor-payment-history`,
      label: 'doctor-payment-history',
      allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
      icon: <FaDollarSign />,
    },
    {
      title: 'Overview',
      path: `/doctor-details/${doctorId}/overview`,
      label: 'overview',
      allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN],
      icon: <FaUser />,
    },
  ]

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleEditPress = () => setIsModalOpen(!isModalOpen)

  const handleSubmit = async (values: any, resetForm: () => void) => {
    setIsSubmitting(true)
    try {
      const resp = await axios.put(API_ENDPOINTS.UPDATE_DOCTOR(userData.id), values)
      notifySuccess(resp.data.message)
      setUserData((prev: any) => ({
        ...prev,
        ...values,
        full_name: `${values.first_name} ${values.last_name}`,
      }))
    } catch (err: any) {
      notifyError(err.response?.data?.message)
    } finally {
      setIsModalOpen(false)
      setIsSubmitting(false)
      resetForm()
    }
  }

  if (loading) {
  return (
    <div style={{height: '80vh'}}>
      <Loader size='lg' color='primary' message='Loading doctor details...' />
    </div>
  )
}

  return (
    <>
      <Routes>
        <Route
          element={
            <>
              <ProfileDetailHeader
                user={userData}
                uuid={doctorId || ''}
                links={links}
              />
              <Outlet />
            </>
          }
        >
          <Route index element={<Navigate to={`/doctor-details/${doctorId}/patients`} replace />} />

          <Route
            path='overview'
            element={
              <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}>
                <PageTitle breadcrumbs={accountBreadCrumbs}>Overview</PageTitle>
                <ProfileDetailOverview
                  user={userData}
                  isLoading={loading}
                  onEditPress={handleEditPress}
                />
              </RoutesRoleGuard>
            }
          />

          <Route
            path='devices'
            element={
              <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}>
                <PageTitle breadcrumbs={accountBreadCrumbs}>Devices</PageTitle>
                <DoctorProfileDevicesPage isLoading={loading} />
              </RoutesRoleGuard>
            }
          />

          <Route
            path='patients'
            element={
              <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}>
                <PageTitle breadcrumbs={accountBreadCrumbs}>Patients</PageTitle>
                <AllPatientsListingProfilePage />
              </RoutesRoleGuard>
            }
          />

          <Route
            path='doctor-payment-history'
            element={
              <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}>
                <PageTitle breadcrumbs={accountBreadCrumbs}>Payment History</PageTitle>
                <DoctorPaymentHistory />
              </RoutesRoleGuard>
            }
          />

          <Route path='*' element={<Navigate to='/error/404' replace />} />
        </Route>
      </Routes>
      <DoctorForm
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        handleSubmit={handleSubmit}
        initialData={userData}
        title='Doctor Form'
      />
    </>
  )
}

export default DoctorDetailsPage
